import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Navbar from "../components/layout/Navbar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCloudSun, faCalendarAlt, faMoneyBillWave, faRoute, faHotel, faEllipsisH, faHome, faPlus, faPen, faCopy } from "@fortawesome/free-solid-svg-icons";
import { clearImageCache } from "../services/imageService";
import {
  generatePackingChecklist as fetchPackingChecklist,
  updateTrip,
  getTrip,
  undoTripChange,
  getCommunityPostDetail,
} from "../services/api";
import {
  LoadingState,
  ImageGalleryGrid,
  ImageGalleryModal,
  PackageCard,
  TripHighlightsSection,
  VisaSection,
  HotelInformationSection,
  DayWiseItinerarySection,
  PackingChecklistSection,
  WeatherAnalysisSection,
  ImportantNotesSection,
  TermsConditionsSection,
  ActionButtonsSection,
  ItinerarySidebar,
  DownloadIcon,
  TripSummaryHeader,
  OccupancyAccordion,
  TransportPackageCard,
  CompareDetailsAccordion,
  useGalleryKeyboard,
  generateItineraryPDF,
  calculateCostSummary,
  TripOverviewSection,
  DesktopTripSummary,
  DesktopOccupancyAccordion,
  DesktopTransportPackage,
  DesktopPackageCard,
  DesktopCompareDetails,
  NotesAccordion,
} from "../components/itinerary";
import { trackEvent } from "../utils/analytics";
import { getPlatform } from "../utils/analytics";
import { createCostEstimate, refreshCostEstimate } from "../services/costEstimationApi";
import { faSync, faExclamationTriangle, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { hydrateStopsFromTrip } from "../utils/stopsMigration";
import * as Sentry from "@sentry/react";
import { AIError } from "../errors/sentryErrors";

export default function Itinerary() {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const [itinerary, setItinerary] = useState(null);
  const [tripData, setTripData] = useState(null);
  const [saved, setSaved] = useState(false);
  const [isFromDashboard, setIsFromDashboard] = useState(false);
  const [tripId, setTripId] = useState(null);
  const [copiedPostInfo, setCopiedPostInfo] = useState(null);
  const [showDatesModal, setShowDatesModal] = useState(false);
  const [newStartDate, setNewStartDate] = useState("");
  const [newEndDate, setNewEndDate] = useState("");
  const [isConfirmingDates, setIsConfirmingDates] = useState(false);
  const [expandedDays, setExpandedDays] = useState({});
  const [showGallery, setShowGallery] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [packingChecklistData, setPackingChecklistData] = useState(null);
  const [isLoadingChecklist, setIsLoadingChecklist] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [originalItinerary, setOriginalItinerary] = useState(null);
  // Tracked separately from originalItinerary because startDate/endDate live
  // on tripData, not itinerary — without this, the dates-modal fix below was
  // getting silently overwritten by the itinerary-diff effect whenever the
  // day-wise content happened to compare equal (e.g. an empty dayWiseItinerary).
  const [originalTripData, setOriginalTripData] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [checkedPackingItems, setCheckedPackingItems] = useState({});
  const [isSavingToDb, setIsSavingToDb] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [isUndoing, setIsUndoing] = useState(false);
  const scrollRestoredRef = useRef(false);

  const [estimateData, setEstimateData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Console log test requirement
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [warningsOpen, setWarningsOpen] = useState(false);
  const [refreshCooldown, setRefreshCooldown] = useState(false);

  const [isGlobalEditMode, setIsGlobalEditMode] = useState(false);
  const [requiresVisa, setRequiresVisa] = useState(true);
  const [hotelsExpanded, setHotelsExpanded] = useState(false);
  const [fetchedHotels, setFetchedHotels] = useState(null);
  const [activeMobileTab, setActiveMobileTab] = useState("plan");
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [activeDay, setActiveDay] = useState(0);

  const handleNavigationRequest = (target, label, state = null) => {
    setPendingNavigation({ target, label, state });
  };

  const itineraryViewedTracked = useRef(false);

  useEffect(() => {
    if (itinerary && tripData && !itineraryViewedTracked.current) {
      itineraryViewedTracked.current = true;

      trackEvent("itinerary_viewed", {
        trip_id: tripId,
        destinations: tripData.destinations?.map((d) => d.name) || [],
        platform: getPlatform(),
      });
    }
  }, [itinerary, tripData, tripId]);

  const handleEstimationError = (err) => {
    const status = err.status;
    let message = "Failed to load pricing.";

    if (status === 401) {
      toast.error("Session expired. Please log in again.");
      navigate("/login");
      return;
    } else if (status === 404) {
      message = "This trip doesn't exist or isn't yours.";
    } else if (status === 429) {
      // Use the backend's specific error message if provided (e.g. "Refresh allowed once per hour")
      message = err.details?.error || "You're requesting prices too fast! Please wait a minute.";
      setRefreshCooldown(true);
      setTimeout(() => setRefreshCooldown(false), 60000); // Keep button disabled for at least 60s
    } else if (status === 500) {
      message = "Pricing temporarily unavailable.";
    } else {
      message = err.details?.error || err.message || message;
    }

    toast.error(message);
    setError(message);
  };

  useEffect(() => {
    if (tripId && !estimateData && !loading && !error) {
      setLoading(true);
      createCostEstimate(tripId)
        .then((response) => {
          setEstimateData(response);
          setLoading(false);
        })
        .catch((err) => {
          Sentry.withScope((scope) => {
            scope.setTag("feature", "ai");
            scope.setTag("app", "web");
            scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
            scope.setContext("trip", { trip_id: tripId });
            Sentry.captureException(new AIError(err instanceof Error ? err.message : String(err)));
          });
          handleEstimationError(err);
          setLoading(false);
        });
    }
  }, [tripId, estimateData, loading, error]);

  // Requirement 1: Calls refreshCostEstimate API
  // Requirement 2: Updates estimateData
  // Requirement 3: Shows loading state via isRefreshing
  // Requirement 4: Shows success toast
  // Requirement 5: Handles 429 refresh rate limit explicitly via handleEstimationError
  const handleRefreshPrices = async () => {
    if (!tripId || isRefreshing || refreshCooldown) return;

    setIsRefreshing(true);
    try {
      const data = await refreshCostEstimate(tripId);
      setEstimateData(data);
      toast.success("Live prices refreshed successfully!");
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "ai");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        scope.setContext("trip", { trip_id: tripId });
        Sentry.captureException(new AIError(err instanceof Error ? err.message : String(err)));
      });
      console.error("[Itinerary] Error refreshing prices:", err);
      handleEstimationError(err);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const handleScroll = () => {
      if (tripId) {
        sessionStorage.setItem(
          `itinerary-scroll-${tripId}`,
          window.scrollY.toString(),
        );
      } else {
        sessionStorage.setItem("itinerary-scroll", window.scrollY.toString());
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [tripId]);

  useEffect(() => {
    if (isDataLoaded && itinerary && !scrollRestoredRef.current) {
      scrollRestoredRef.current = true;
      const scrollKey = tripId
        ? `itinerary-scroll-${tripId}`
        : "itinerary-scroll";
      const savedScroll = sessionStorage.getItem(scrollKey);
      if (savedScroll) {
        setTimeout(() => {
          window.scrollTo({
            top: parseInt(savedScroll, 10),
            behavior: "smooth",
          });
        }, 150);
      }
    }
  }, [isDataLoaded, itinerary, tripId]);

  useEffect(() => {
    const loadPackingChecklist = async () => {
      const hasChecklist =
        itinerary?.packingChecklist &&
        Object.keys(itinerary.packingChecklist).length > 0;
      if (hasChecklist || packingChecklistData || isLoadingChecklist) {
        return;
      }

      if (itinerary && tripData) {
        setIsLoadingChecklist(true);
        try {
          const checklist = await fetchPackingChecklist(tripData, itinerary);
          if (checklist) {
            setPackingChecklistData(checklist);
            setItinerary((prev) => ({ ...prev, packingChecklist: checklist }));
          }
        } catch (error) {
          Sentry.withScope((scope) => {
            scope.setTag("feature", "ai");
            scope.setTag("app", "web");
            scope.setTag("error_source", error instanceof TypeError ? "network" : "server");
            scope.setContext("trip", { trip_id: tripId });
            Sentry.captureException(new AIError(error instanceof Error ? error.message : String(error)));
          });
          console.error("Failed to load packing checklist:", error);
        } finally {
          setIsLoadingChecklist(false);
        }
      }
    };
    loadPackingChecklist();
  }, [
    itinerary?.packingChecklist,
    tripData,
    packingChecklistData,
    isLoadingChecklist,
  ]);

  const loadUiState = useCallback((savedTripId) => {
    const uiData = JSON.parse(localStorage.getItem("itinerary-data") || "{}");
    if (uiData.checkedPackingItems)
      setCheckedPackingItems(uiData.checkedPackingItems);
    const savedExpandedDays = localStorage.getItem("itinerary-expanded-days");
    if (savedExpandedDays) {
      try {
        setExpandedDays(JSON.parse(savedExpandedDays));
      } catch (_) {
        setExpandedDays({ 0: true });
      }
    } else {
      setExpandedDays({ 0: true });
    }
    if (savedTripId) {
      localStorage.setItem(
        "itinerary-data",
        JSON.stringify({
          ...uiData,
          tripId: savedTripId,
        }),
      );
    }
  }, []);

  const applyTripFromState = useCallback(
    (itineraryData, tripDataVal, meta = {}) => {
      clearImageCache();
      setItinerary(itineraryData);
      setTripData(tripDataVal);
      setOriginalItinerary(JSON.parse(JSON.stringify(itineraryData)));
      setOriginalTripData(
        tripDataVal
          ? { startDate: tripDataVal.startDate, endDate: tripDataVal.endDate }
          : null,
      );
      setHasUnsavedChanges(false);
      document.title = `${itineraryData?.tripSummary?.tripTitle || tripDataVal?.destination || "Itinerary"} | Romaya`;
      if (meta.fromDashboard) {
        setIsFromDashboard(true);
        setSaved(true);
      }
      if (meta.alreadySaved) setSaved(true);
      if (meta.tripId) setTripId(meta.tripId);
      if (meta.canUndo !== undefined) setCanUndo(meta.canUndo);
      setIsDataLoaded(true);
    },
    [],
  );

  useEffect(() => {
    const loadFromDb = async (id) => {
      try {
        const trip = await getTrip(id);
        const itineraryData = trip.itinerary;
        const tripDataVal = {
          startingLocation: trip.startingLocation,
          startingLocationData: trip.startingLocationData,
          destination: trip.destination,
          destinationData: trip.destinationData,
          destinations: trip.destinations || [],
          travelStyle: trip.travelStyle || "",
          startDate: trip.startDate?.split("T")[0],
          endDate: trip.endDate?.split("T")[0],
          themes: trip.themes,
          weather: trip.weather,
          accommodation: trip.accommodation,
          food: trip.food,
          travelToDest: trip.travelToDest,
          travelBetween: trip.travelBetween,
          adults: trip.adults,
          children: trip.children,
          infants: trip.infants,
          budget: trip.budget,
          currency: trip.currency,
          preferences: trip.preferences,
          customNightsPerDestination: trip.customNightsPerDestination,
          stops: hydrateStopsFromTrip(trip),
          rooms: trip.rooms,
          copiedFrom: trip.copiedFrom,
        };
        applyTripFromState(itineraryData, tripDataVal, {
          fromDashboard: true,
          alreadySaved: true,
          tripId: id,
          canUndo: !!trip.canUndo,
        });
        loadUiState(id);
      } catch (err) {
        Sentry.withScope((scope) => {
          scope.setTag("feature", "ai");
          scope.setTag("app", "web");
          scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
          scope.setContext("trip", { trip_id: id });
          Sentry.captureException(new AIError(err instanceof Error ? err.message : String(err)));
        });
        console.error("Failed to load trip from DB:", err);
        navigate("/dashboard");
      }
    };

    if (location.state?.itinerary && location.state?.tripData) {
      const meta = {
        fromDashboard: location.state.fromDashboard,
        alreadySaved: location.state.alreadySaved,
        tripId: location.state.tripId,
      };
      applyTripFromState(
        location.state.itinerary,
        location.state.tripData,
        meta,
      );
      loadUiState(location.state.tripId);
    } else if (routeId) {
      loadFromDb(routeId);
    } else {
      const uiData = JSON.parse(localStorage.getItem("itinerary-data") || "{}");
      if (uiData.tripId) {
        loadFromDb(uiData.tripId);
      }
    }
  }, [location.state, routeId, navigate, applyTripFromState, loadUiState]);

  useEffect(() => {
    if (tripData?.copiedFrom?.sourcePostId && !copiedPostInfo) {
      getCommunityPostDetail(tripData.copiedFrom.sourcePostId, true)
        .then(data => {
          if (data && data.post) setCopiedPostInfo(data);
        })
        .catch(err => {
          Sentry.withScope((scope) => {
            scope.setTag("feature", "ai");
            scope.setTag("app", "web");
            scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
            scope.setContext("trip", { trip_id: tripId });
            Sentry.captureException(new AIError(err instanceof Error ? err.message : String(err)));
          });
          console.error("Failed to fetch source post:", err);
        });
    }
    
    if (tripData?.startDate && tripData.startDate.startsWith('1970')) {
      setShowDatesModal(true);
    }
  }, [tripData?.startDate, tripData?.copiedFrom?.sourcePostId, copiedPostInfo]);

  useGalleryKeyboard(
    showGallery,
    setShowGallery,
    setGalleryIndex,
    itinerary?.destinationImages?.length || 1,
  );

  const toggleDay = (index) => {
    setExpandedDays((prev) => {
      const isCurrentlyExpanded = prev[index];
      const newState = isCurrentlyExpanded ? {} : { [index]: true };
      localStorage.setItem("itinerary-expanded-days", JSON.stringify(newState));
      return newState;
    });
  };

  const handleAddActivity = (dayIndex, newActivity) => {
    setItinerary((prev) => {
      const updated = { ...prev };
      const updatedDays = [...updated.dayWiseItinerary];

      if (!updatedDays[dayIndex].activities) {
        updatedDays[dayIndex].activities = [];
      }

      updatedDays[dayIndex].activities.push(newActivity);

      updatedDays[dayIndex].activities.sort((a, b) => {
        const timeA = a.time || "00:00";
        const timeB = b.time || "00:00";
        return timeA.localeCompare(timeB);
      });

      updated.dayWiseItinerary = updatedDays;
      return updated;
    });
    setHasUnsavedChanges(true);
  };

  const handleEditActivity = (dayIndex, activityIndex, updatedActivity) => {
    setItinerary((prev) => {
      const updated = { ...prev };
      const updatedDays = [...updated.dayWiseItinerary];

      if (updatedDays[dayIndex]?.activities?.[activityIndex]) {
        updatedDays[dayIndex].activities[activityIndex] = {
          ...updatedDays[dayIndex].activities[activityIndex],
          ...updatedActivity,
        };

        updatedDays[dayIndex].activities.sort((a, b) => {
          const timeA = a.time || "00:00";
          const timeB = b.time || "00:00";
          return timeA.localeCompare(timeB);
        });
      }

      updated.dayWiseItinerary = updatedDays;
      return updated;
    });
    setHasUnsavedChanges(true);
  };

  const handleDeleteActivity = (dayIndex, activityIndex) => {
    setItinerary((prev) => {
      const updated = { ...prev };
      const updatedDays = [...updated.dayWiseItinerary];

      if (updatedDays[dayIndex]?.activities) {
        updatedDays[dayIndex].activities = updatedDays[
          dayIndex
        ].activities.filter((_, idx) => idx !== activityIndex);
      }

      updated.dayWiseItinerary = updatedDays;
      return updated;
    });
    setHasUnsavedChanges(true);
  };

  const handleUpdateDay = (dayIndex, updates) => {
    setItinerary((prev) => {
      const updated = { ...prev };
      const updatedDays = [...updated.dayWiseItinerary];

      if (updatedDays[dayIndex]) {
        updatedDays[dayIndex] = {
          ...updatedDays[dayIndex],
          ...updates,
          ...(updates.activities !== undefined
            ? { activities: updates.activities }
            : {}),
        };
      }

      updated.dayWiseItinerary = updatedDays;
      return updated;
    });
    setHasUnsavedChanges(true);
  };

  const handleRevertToOriginal = () => {
    if (!originalItinerary) return;

    if (
      window.confirm(
        "Are you sure you want to revert to the original itinerary? All your changes will be lost.",
      )
    ) {
      setItinerary(JSON.parse(JSON.stringify(originalItinerary)));
      setHasUnsavedChanges(false);
    }
  };

  const updateItinerary = (updates) => {
    setItinerary((prev) => ({ ...prev, ...updates }));
    setHasUnsavedChanges(true);
  };

  const handleUpdateOverview = (newOverview) => {
    updateItinerary({
      tripSummary: { ...itinerary.tripSummary, overview: newOverview },
    });
  };

  const handleUpdateCitiesBreakdown = (updatedCities) => {
    updateItinerary({
      tripSummary: { ...itinerary.tripSummary, citiesBreakdown: updatedCities },
      cachedMapData: null,
    });
  };

  const handleUpdateImportantNotes = (updatedNotes) => {
    updateItinerary({ importantNotes: updatedNotes });
  };

  const handleUpdatePackingChecklist = (updatedChecklist) => {
    updateItinerary({ packingChecklist: updatedChecklist });
  };

  const handleCheckedPackingItemsChange = useCallback((newCheckedItems) => {
    setCheckedPackingItems(newCheckedItems);
    const savedData = JSON.parse(
      localStorage.getItem("itinerary-data") || "{}",
    );
    savedData.checkedPackingItems = newCheckedItems;
    localStorage.setItem("itinerary-data", JSON.stringify(savedData));
  }, []);

  const handleMapDataGenerated = (mapData) => {
    if (!itinerary?.cachedMapData) {
      updateItinerary({ cachedMapData: mapData });
    }
  };

  const handleDownloadPDF = async () => {
    Sentry.addBreadcrumb({
      category: "user-action",
      message: "Downloaded trip PDF",
      level: "info",
    });
    trackEvent("itinerary_download_clicked", {
      trip_id: tripId,
      destinations: tripData.destinations?.map((d) => d.name).filter(Boolean).join(', ') || tripData.destination || '',
    });

    const finalCostSummary = itinerary.costSummary?.estimatedTotal
      ? itinerary.costSummary
      : calculateCostSummary(itinerary, tripData);
      
    setIsGeneratingPDF(true);
    try {
      await generateItineraryPDF(
        itinerary,
        tripData,
        finalCostSummary,
        checkedPackingItems,
        fetchedHotels
      );
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "ai");
        scope.setTag("app", "web");
        scope.setTag("error_source", "browser");
        scope.setContext("trip", { trip_id: tripId });
        Sentry.captureException(new AIError(error instanceof Error ? error.message : String(error)));
      });
      console.error("Failed to generate PDF:", error);
      toast.error("Failed to download itinerary. Please try again.");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  useEffect(() => {
    if (itinerary && originalItinerary) {
      const getComparableData = (data) => {
        if (!data) return {};
        return {
          tripSummary: {
            overview: data.tripSummary?.overview,
            citiesBreakdown: data.tripSummary?.citiesBreakdown,
          },
          dayWiseItinerary: data.dayWiseItinerary,
          importantNotes: data.importantNotes,
          packingChecklist: data.packingChecklist,
          accommodationDetails: data.accommodationDetails,
        };
      };

      const currentEditable = getComparableData(itinerary);
      const originalEditable = getComparableData(originalItinerary);
      const itineraryChanged =
        JSON.stringify(currentEditable) !== JSON.stringify(originalEditable);
      const datesChanged =
        !!originalTripData &&
        (tripData?.startDate !== originalTripData.startDate ||
          tripData?.endDate !== originalTripData.endDate);
      setHasUnsavedChanges(itineraryChanged || datesChanged);
    }
  }, [itinerary, originalItinerary, tripData, originalTripData]);

  const handleSavePermanently = async () => {
    Sentry.addBreadcrumb({
      category: "user-action",
      message: "Saved trip",
      level: "info",
    });
    if (!tripId) {
      toast.error(
        "Cannot save: Trip ID not found. Please save the trip first from the dashboard.",
      );
      return;
    }

    setIsSavingToDb(true);

    try {
      await updateTrip(tripId, tripData, itinerary);

      setOriginalItinerary(JSON.parse(JSON.stringify(itinerary)));
      setOriginalTripData({ startDate: tripData?.startDate, endDate: tripData?.endDate });
      setHasUnsavedChanges(false);
      setCanUndo(true);
      trackEvent("trip_saved", {
        trip_id: tripId,
        destinations: tripData.destinations?.map((d) => d.name).filter(Boolean).join(', ') || tripData.destination || '',
      });
      toast.success("Changes saved successfully!");
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "ai");
        scope.setTag("app", "web");
        scope.setTag("error_source", error instanceof TypeError ? "network" : "server");
        scope.setContext("trip", { trip_id: tripId });
        Sentry.captureException(new AIError(error instanceof Error ? error.message : String(error)));
      });
      console.error("Failed to save itinerary:", error);
      toast.error(error.message || "Failed to save changes. Please try again.");
    } finally {
      setIsSavingToDb(false);
    }
  };

  const handleUndo = async () => {
    Sentry.addBreadcrumb({
      category: "user-action",
      message: "Undid trip changes",
      level: "info",
    });
    if (!tripId || isUndoing) return;
    setIsUndoing(true);
    try {
      const result = await undoTripChange(tripId);
      setItinerary(result.itinerary);
      setOriginalItinerary(JSON.parse(JSON.stringify(result.itinerary)));
      setHasUnsavedChanges(false);
      setCanUndo(result.canUndo);
      toast.success("Last change undone!");
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "ai");
        scope.setTag("app", "web");
        scope.setTag("error_source", error instanceof TypeError ? "network" : "server");
        scope.setContext("trip", { trip_id: tripId });
        Sentry.captureException(new AIError(error instanceof Error ? error.message : String(error)));
      });
      toast.error(error.message || "Failed to undo. Please try again.");
    } finally {
      setIsUndoing(false);
    }
  };

  if (!itinerary) {
    return <LoadingState />;
  }

  const {
    tripSummary,
    accommodationDetails,
    dayWiseItinerary,
    importantNotes,
    bookingLinks,
    destinationImages,
    costSummary,
    localServices,
    packingChecklist,
    weatherAnalysis,
  } = itinerary;

  const finalCostSummary = costSummary?.estimatedTotal
    ? costSummary
    : calculateCostSummary(itinerary, tripData);

  const handleImageClick = (index) => {
    setShowGallery(true);
    setGalleryIndex(index);
  };

  return (
    <div className="min-h-screen bg-gray-100 pb-20 xl:pb-0">
      <Navbar
        rightAction={
          <button
            onClick={handleDownloadPDF}
            disabled={isGeneratingPDF}
            className="inline-flex items-center gap-2 rounded-full bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-teal-700 disabled:opacity-75 disabled:cursor-wait cursor-pointer"
          >
            {isGeneratingPDF ? (
              <>
                <svg className="w-4 h-4 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Generating PDF...
              </>
            ) : (
              <>
                <DownloadIcon />
                Download Itinerary
              </>
            )}
          </button>
        }
      />

      <ItinerarySidebar
        requiresVisa={requiresVisa}
        onEditTrip={() =>
          handleNavigationRequest("/plan", "Edit Trip", {
            editTrip: tripData,
            tripId,
            existingItinerary: itinerary,
          })
        }
        onBackToDashboard={
          isFromDashboard ? () => handleNavigationRequest("/dashboard", "Dashboard") : null
        }
        onGoToDashboard={!isFromDashboard ? () => handleNavigationRequest("/dashboard", "Dashboard") : null}
        onPlanAnotherTrip={!isFromDashboard ? () => handleNavigationRequest("/plan", "Plan Another Trip") : null}
      />

      <div id="itinerary-content" className="pt-16 lg:pt-20 xl:pl-60">
        <div className="px-3 sm:px-4 lg:px-6 xl:px-8 pt-4 pb-2 lg:pt-6 lg:pb-4 flex items-center gap-2 text-xs sm:text-sm text-gray-500 font-medium whitespace-nowrap overflow-x-auto w-full scrollbar-hidden">
          <button onClick={() => handleNavigationRequest("/dashboard", "Dashboard")} className="hover:text-teal-600 transition-colors cursor-pointer shrink-0">Dashboard</button>
          <span className="text-gray-300 shrink-0">/</span>
          <button
            onClick={() => {
              handleNavigationRequest("/plan", isFromDashboard ? "My Trips" : "Plan Trip", { editTrip: tripData, tripId, existingItinerary: itinerary });
            }}
            className="hover:text-teal-600 transition-colors cursor-pointer shrink-0"
          >
            {isFromDashboard ? "My Trips" : "Plan Trip"}
          </button>
          <span className="text-gray-300 shrink-0">/</span>
          <span className="text-teal-700 font-semibold shrink-0">Itinerary</span>
        </div>
        
        {copiedPostInfo && (
          <div className="mx-3 sm:mx-4 lg:mx-6 xl:mx-8 mb-4 bg-teal-50 border border-teal-100 rounded-xl p-3 flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-2 text-sm text-teal-800">
              <span className="text-teal-600 bg-white rounded-full w-6 h-6 flex items-center justify-center border border-teal-100 shadow-sm">
                <FontAwesomeIcon icon={faCopy} className="text-xs" />
              </span>
              <span>
                Copied from <span className="font-semibold">{copiedPostInfo.trip?.destination}</span>. Customize dates and details, then book with our AI planner.
              </span>
            </div>
            <button 
              onClick={() => navigate(`/community/${copiedPostInfo.post?.slug}`)}
              className="text-xs font-semibold bg-white text-teal-700 px-3 py-1.5 rounded-lg border border-teal-200 hover:bg-teal-100 transition-colors"
            >
              View Original
            </button>
          </div>
        )}

        <div id="section-gallery" className={`scroll-mt-[100px] ${activeMobileTab === 'plan' ? 'block' : 'hidden'} lg:block`}>
          <ImageGalleryGrid
            destinationImages={destinationImages}
            tripSummary={tripSummary}
            onImageClick={handleImageClick}
          />
          <div id="section-visa" className="empty:hidden scroll-mt-[100px] mt-6 px-3 sm:px-4 lg:px-6 xl:px-8">
            <VisaSection
              tripData={tripData}
              onVisaStatusChange={setRequiresVisa}
            />
          </div>
        </div>

        {showGallery && destinationImages && (
          <ImageGalleryModal
            destinationImages={destinationImages}
            tripSummary={tripSummary}
            galleryIndex={galleryIndex}
            setGalleryIndex={setGalleryIndex}
            onClose={() => setShowGallery(false)}
          />
        )}

        <div
          className={`w-full px-3 sm:px-4 lg:px-6 xl:px-8 xl:pr-8 pt-4 pb-28 lg:py-8 ${hasUnsavedChanges ? "pb-40 lg:pb-32" : ""}`}
        >

          <div className={`${activeMobileTab === 'plan' ? 'block' : 'hidden'} lg:block`}>
            <TripOverviewSection
              tripSummary={tripSummary}
              tripData={tripData}
              onUpdateOverview={handleUpdateOverview}
              onUpdateCitiesBreakdown={handleUpdateCitiesBreakdown}
              cachedMapData={itinerary?.cachedMapData}
              onMapDataGenerated={handleMapDataGenerated}
              originalOverview={originalItinerary?.tripSummary?.overview}
              originalCitiesBreakdown={
                originalItinerary?.tripSummary?.citiesBreakdown
              }
            />
          </div>

          <div id="section-weather" className={`scroll-mt-[100px] ${activeMobileTab === 'weather' ? 'block' : 'hidden'} lg:block`}>
            <WeatherAnalysisSection
              weatherAnalysis={weatherAnalysis}
              tripSummary={tripSummary}
            />
          </div>

          <div id="section-budget" className={`scroll-mt-[100px] ${activeMobileTab === 'budget' ? 'block' : 'hidden'} lg:block`}>
            {/* Header Section */}
            <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate(-1)}
                  className="bg-white border border-gray-200 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] text-gray-700 w-9 h-9 rounded-full flex items-center justify-center hover:bg-gray-50 hover:text-teal-600 transition-all active:scale-95 md:hidden"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                </button>
                <div className="flex flex-col">
                  <h2 className="text-xl md:text-2xl font-black text-gray-900 tracking-tight">Cost Estimation</h2>
                  <span className="text-xs md:hidden text-gray-500 mt-0.5">Choose your ideal package</span>
                </div>
              </div>
            </div>
            {/* MOBILE LAYOUT */}
            <div className="lg:hidden">
              <TripSummaryHeader tripData={tripData} itinerary={itinerary} />
              <OccupancyAccordion estimateData={estimateData} />
              <DesktopTransportPackage
                tripData={tripData}
                estimateData={estimateData}
                onRefresh={handleRefreshPrices}
                isRefreshing={isRefreshing}
              />
            </div>

            {/* DESKTOP LAYOUT (Top Section) */}
            <div className="hidden lg:block mb-8">
              <DesktopTripSummary
                tripData={tripData}
                itinerary={itinerary}
                estimateData={estimateData}
                onRefresh={handleRefreshPrices}
                isRefreshing={isRefreshing}
                loading={loading}
                refreshCooldown={refreshCooldown}
              />
              <div className="mt-6">
                <DesktopTransportPackage
                  tripData={tripData}
                  estimateData={estimateData}
                  onRefresh={handleRefreshPrices}
                  isRefreshing={isRefreshing}
                />
              </div>
            </div>

            {/* Loop through packages and render cards */}
            <div className="mb-4">
              {/* MOBILE HEADER */}
              <div className="flex lg:hidden justify-between items-center mb-3 ml-1">
                <h3 className="font-bold text-gray-900 text-sm md:text-base">Land Package Options</h3>
                {estimateData?.totalPax > 0 && (
                  <button
                    onClick={handleRefreshPrices}
                    disabled={isRefreshing || loading || refreshCooldown}
                    className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 text-gray-600 rounded-lg text-xs font-bold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FontAwesomeIcon icon={faSync} className={isRefreshing ? "animate-spin" : ""} />
                    {isRefreshing ? 'Refreshing...' : 'Refresh Prices'}
                  </button>
                )}
              </div>

              {/* DESKTOP HEADER */}
              <div className="hidden lg:flex justify-between items-end mb-6">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">Land Package Options</h2>
                </div>
                <div className="text-xs text-gray-500 font-medium mb-1">
                  Prices are inclusive of taxes and subject to availability.
                </div>
              </div>

              <div className="space-y-4">
                {estimateData?.totalPax === 0 ? (
                  <div className="p-6 bg-orange-50 border border-orange-200 rounded-xl text-center">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-orange-400 text-3xl mb-3" />
                    <h4 className="font-bold text-gray-900 mb-2">Pricing Unavailable</h4>
                    <p className="text-gray-600 mb-4 text-sm">Set up rooms in Step 3 to see accurate pricing.</p>
                    <button
                      onClick={() => navigate('/plan-trip?step=3')}
                      className="px-4 py-2 bg-white text-orange-600 font-bold border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
                    >
                      Configure Travelers
                    </button>
                  </div>
                ) : estimateData?.packages?.length > 0 ? (
                  <>
                    {/* MOBILE PACKAGE CARDS */}
                    <div className="lg:hidden space-y-4">
                      {estimateData.packages.map((pkg, idx) => (
                        <PackageCard
                          key={idx}
                          pkg={pkg}
                          totalTravelers={estimateData?.totalPax}
                          includeTransport={(estimateData?.includeTransport ?? estimateData?.input?.includeTransport ?? estimateData?.transport?.includeTransport) !== false}
                          currency={estimateData?.currency}
                          isBestValue={idx === 0}
                          isMostPopular={idx === 1}
                          tripData={tripData}
                        />
                      ))}
                    </div>
                    {/* DESKTOP PACKAGE CARDS GRID */}
                    <div className="hidden lg:grid grid-cols-2 gap-6">
                      {estimateData.packages.map((pkg, idx) => (
                        <DesktopPackageCard
                          key={idx}
                          pkg={pkg}
                          estimateData={estimateData}
                          tripData={tripData}
                          isBestValue={idx === 0}
                          isMostPopular={idx === 1}
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="p-6 bg-gray-50 border border-gray-200 rounded-xl text-center">
                    <p className="text-gray-500 font-medium">Waiting for packages to generate...</p>
                  </div>
                )}
              </div>
            </div>

            {/* Compare Details Accordion (MOBILE ONLY) */}
            <div className="lg:hidden">
              <CompareDetailsAccordion estimateData={estimateData} />
            </div>

            {/* Compare Details Table (DESKTOP ONLY) */}
            <div className="hidden lg:block">
              <DesktopCompareDetails estimateData={estimateData} />
            </div>

            {/* Warnings Accordion */}
            <NotesAccordion estimateData={estimateData} />

            {/* Disclaimer Section */}
            {estimateData?.disclaimer && (
              <div className="mt-4 lg:mt-2 pt-4 lg:pt-2 border-t border-gray-200">
                <p className="text-[11px] md:text-xs text-gray-400 leading-relaxed text-center italic">
                  {estimateData.disclaimer}
                </p>
              </div>
            )}
          </div>

          <div className={`${activeMobileTab === 'itinerary' ? 'block' : 'hidden'} lg:block`}>
            <div id="section-highlights" className="scroll-mt-[100px]">
              <TripHighlightsSection
                dayWiseItinerary={dayWiseItinerary}
                destinationImages={destinationImages}
                activeDay={activeDay}
                setActiveDay={setActiveDay}
                setExpandedDays={setExpandedDays}
              />
            </div>



            <div id="section-itinerary" className="scroll-mt-[100px]">
              <DayWiseItinerarySection
                dayWiseItinerary={dayWiseItinerary}
                expandedDays={expandedDays}
                setExpandedDays={setExpandedDays}
                toggleDay={toggleDay}
                activeDay={activeDay}
                bookingLinks={bookingLinks}
                tripSummary={tripSummary}
                localServices={localServices}
                travelPreferences={
                  itinerary.travelPreferences || {
                    travelToDest: tripData?.travelToDest,
                    travelBetween: tripData?.travelBetween,
                    dietaryPreferences: Array.isArray(tripData?.food)
                      ? tripData.food.join(", ")
                      : tripData?.dietaryPreferences || tripData?.dietary || "",
                  }
                }
                onAddActivity={handleAddActivity}
                onEditActivity={handleEditActivity}
                onDeleteActivity={handleDeleteActivity}
                onUpdateDay={handleUpdateDay}
                originalDayWiseItinerary={originalItinerary?.dayWiseItinerary}
                currency={tripData?.currency || "INR"}
              />
            </div>
          </div>

          <div className={`${activeMobileTab === 'hotels' ? 'block' : 'hidden'} lg:block`}>
            <div id="section-hotels" className="scroll-mt-[100px]">
              <HotelInformationSection
                accommodationDetails={accommodationDetails}
                tripData={tripData}
                bookingLinks={bookingLinks}
                tripSummary={tripSummary}
                dayWiseItinerary={dayWiseItinerary}
                isExpanded={hotelsExpanded}
                onToggleExpand={() => setHotelsExpanded((prev) => !prev)}
                onHotelsLoaded={(hotels) => setFetchedHotels(hotels)}
              />
            </div>
          </div>

          <div className={`${activeMobileTab === 'plan' ? 'block' : 'hidden'} lg:block`}>

            <div id="section-notes" className="scroll-mt-[100px]">
              <ImportantNotesSection
                importantNotes={importantNotes}
                isEditMode={isGlobalEditMode}
                onUpdate={handleUpdateImportantNotes}
              />
            </div>

            <TermsConditionsSection
              expandedDays={expandedDays}
              setExpandedDays={setExpandedDays}
            />

            <div id="section-packing" className="scroll-mt-[100px]">
              {isLoadingChecklist ? (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-gray-600">
                      Generating your personalized packing checklist...
                    </span>
                  </div>
                </div>
              ) : (
                <PackingChecklistSection
                  packingChecklist={packingChecklist || packingChecklistData}
                  tripId={tripId}
                  onUpdate={handleUpdatePackingChecklist}
                  checkedItemsData={checkedPackingItems}
                  onCheckedItemsChange={handleCheckedPackingItemsChange}
                  originalPackingChecklist={originalItinerary?.packingChecklist}
                />
              )}
            </div>

            <ActionButtonsSection
              saved={saved}
              isFromDashboard={isFromDashboard}
              tripData={tripData}
              tripId={tripId}
              itinerary={itinerary}
            />
          </div>
        </div>
      </div>

      {(hasUnsavedChanges || canUndo) && (
        <div className="fixed bottom-20 lg:bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-gray-200 shadow-lg z-50 transform transition-transform duration-300">
          <div className="w-full px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {hasUnsavedChanges ? (
                <>
                  <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-gray-600">
                    You have unsaved changes
                  </span>
                </>
              ) : (
                <span className="text-sm text-gray-500">Trip saved</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {canUndo && !hasUnsavedChanges && (
                <button
                  onClick={handleUndo}
                  disabled={isUndoing}
                  className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 disabled:opacity-50 rounded-lg transition-colors cursor-pointer flex items-center gap-2"
                >
                  {isUndoing ? (
                    <svg
                      className="w-4 h-4 animate-spin"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      ></path>
                    </svg>
                  ) : (
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                      />
                    </svg>
                  )}
                  Undo Last Change
                </button>
              )}
              {hasUnsavedChanges && (
                <>
                  <button
                    onClick={handleRevertToOriginal}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                  >
                    Revert All
                  </button>
                  <button
                    onClick={handleSavePermanently}
                    disabled={isSavingToDb || !tripId}
                    className="px-6 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg transition-colors cursor-pointer flex items-center gap-2"
                  >
                    {isSavingToDb ? (
                      <>
                        <svg
                          className="w-4 h-4 animate-spin"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                          />
                        </svg>
                        Save Permanently
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
          {!tripId && hasUnsavedChanges && (
            <div className="bg-amber-50 border-t border-amber-200 px-4 py-2">
              <p className="text-xs text-amber-700 text-center">
                This trip hasn't been saved to your account yet. Save it from
                the actions below first.
              </p>
            </div>
          )}
        </div>
      )}

      {loading && (
        <div className="fixed bottom-4 right-4 bg-white shadow-xl rounded-lg p-4 flex items-center gap-3 z-50 border border-teal-100">
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-teal-500 border-t-transparent"></div>
          <p className="text-teal-800 font-medium text-sm">Generating cost estimates...</p>
        </div>
      )}

      {/* Main Content Area */}
      {/* Mobile More Menu Popup */}
      {showMoreMenu && (
        <div
          className="fixed inset-0 z-[55] bg-black/20 lg:hidden"
          onClick={() => setShowMoreMenu(false)}
        >
          <div
            className="absolute bottom-[80px] right-4 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 w-56 transform transition-all"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => {
                setShowMoreMenu(false);
                handleNavigationRequest("/dashboard", "Dashboard");
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-teal-700 font-medium hover:bg-teal-50 transition-colors"
            >
              <FontAwesomeIcon icon={faHome} className="w-4" />
              Go to Dashboard
            </button>
            <div className="h-px bg-gray-100 my-1"></div>
            <button
              onClick={() => {
                setShowMoreMenu(false);
                trackEvent("plan_another_trip_clicked");
                handleNavigationRequest("/plan", "Plan Another Trip");
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-600 font-medium hover:bg-gray-50 transition-colors"
            >
              <FontAwesomeIcon icon={faPlus} className="w-4" />
              Plan Another Trip
            </button>
            <div className="h-px bg-gray-100 my-1"></div>
            <button
              onClick={() => {
                setShowMoreMenu(false);
                trackEvent("modify_trip_clicked", { trip_id: tripId });
                handleNavigationRequest("/plan", "Modify Trip", {
                  editTrip: tripData,
                  tripId,
                  existingItinerary: itinerary,
                });
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-600 font-medium hover:bg-gray-50 transition-colors"
            >
              <FontAwesomeIcon icon={faPen} className="w-4" />
              Modify Trip
            </button>
          </div>
        </div>
      )}

      {showDatesModal && (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl relative border border-gray-100">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Set Your Travel Dates</h3>
            <p className="text-gray-600 mb-6 text-sm">
              Since you copied this itinerary, please select your own travel dates to continue planning and saving.
            </p>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  min={new Date().toISOString().split('T')[0]}
                  value={newStartDate}
                  onChange={(e) => setNewStartDate(e.target.value)}
                  className="w-full border border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  min={newStartDate || new Date().toISOString().split('T')[0]}
                  value={newEndDate}
                  onChange={(e) => setNewEndDate(e.target.value)}
                  className="w-full border border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                />
              </div>
            </div>
            <button
              disabled={!newStartDate || !newEndDate || newStartDate > newEndDate || isConfirmingDates}
              onClick={async () => {
                const newStartDateObj = new Date(newStartDate);
                const updatedDayWiseItinerary = itinerary?.dayWiseItinerary?.map((day, idx) => {
                  const dayDate = new Date(newStartDateObj);
                  dayDate.setDate(dayDate.getDate() + idx);
                  return { ...day, date: dayDate.toISOString() };
                }) || [];

                const newTripData = { ...tripData, startDate: newStartDate, endDate: newEndDate };
                const newItinerary = { ...itinerary, dayWiseItinerary: updatedDayWiseItinerary };

                // Persist immediately rather than only staging local state and
                // relying on the user separately noticing and clicking the
                // floating Save bar afterward — that two-step dependency was
                // why fixed dates could be silently lost (see the effect
                // above that owns hasUnsavedChanges).
                if (tripId) {
                  setIsConfirmingDates(true);
                  try {
                    await updateTrip(tripId, newTripData, newItinerary);
                    setTripData(newTripData);
                    setItinerary(newItinerary);
                    setOriginalItinerary(JSON.parse(JSON.stringify(newItinerary)));
                    setOriginalTripData({ startDate: newTripData.startDate, endDate: newTripData.endDate });
                    setHasUnsavedChanges(false);
                    setShowDatesModal(false);
                    toast.success("Travel dates saved!");
                  } catch (error) {
                    Sentry.withScope((scope) => {
                      scope.setTag("feature", "ai");
                      scope.setTag("app", "web");
                      scope.setTag("error_source", error instanceof TypeError ? "network" : "server");
                      scope.setContext("trip", { trip_id: tripId });
                      Sentry.captureException(new AIError(error instanceof Error ? error.message : String(error)));
                    });
                    // Fall back to the old behavior: keep the picked dates
                    // locally and let the Save bar pick up the change, so at
                    // least this attempt isn't a dead end.
                    setTripData(newTripData);
                    setItinerary(newItinerary);
                    setHasUnsavedChanges(true);
                    setShowDatesModal(false);
                    toast.error(error?.message || "Couldn't save your dates automatically — click Save to try again.");
                  } finally {
                    setIsConfirmingDates(false);
                  }
                } else {
                  setTripData(newTripData);
                  setItinerary(newItinerary);
                  setHasUnsavedChanges(true);
                  setShowDatesModal(false);
                }
              }}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white font-medium py-2.5 px-4 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isConfirmingDates ? "Saving…" : "Confirm Dates"}
            </button>
          </div>
        </div>
      )}

      {/* Custom Navigation Confirmation Modal */}
      {pendingNavigation && (
        <div className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-[320px] overflow-hidden transform transition-all scale-100">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4 border-[4px] border-teal-50/50">
                <FontAwesomeIcon icon={pendingNavigation.target === "/dashboard" ? faHome : faRoute} className="text-2xl text-teal-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {pendingNavigation.target === "/dashboard" ? "Go to Dashboard?" : `Go to ${pendingNavigation.label}?`}
              </h3>
              <p className="text-gray-500 text-[13px] leading-relaxed mb-6">
                Are you sure you want to leave this page and {pendingNavigation.target === "/dashboard" ? "return to the dashboard" : "go back"}?
                {hasUnsavedChanges && (
                  <span className="block mt-2 font-medium text-amber-600">
                    Warning: You have unsaved changes.
                  </span>
                )}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setPendingNavigation(null)}
                  className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 transition-colors text-sm cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const nav = pendingNavigation;
                    setPendingNavigation(null);
                    if (nav.state) {
                      navigate(nav.target, { state: nav.state });
                    } else {
                      navigate(nav.target);
                    }
                  }}
                  className="flex-1 px-4 py-3 rounded-xl bg-teal-600 text-white font-semibold hover:bg-teal-700 transition-colors shadow-sm text-sm cursor-pointer"
                >
                  Yes, go
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-[0_-10px_40px_rgba(0,0,0,0.04)] z-[60] lg:hidden pb-safe">
        <div className="flex items-center justify-around h-[72px] px-1">
          {[
            { id: "plan", label: "Plan", icon: faCalendarAlt },
            { id: "budget", label: "Budget", icon: faMoneyBillWave },
            { id: "weather", label: "Weather", icon: faCloudSun },
            { id: "itinerary", label: "Itinerary", icon: faRoute },
            { id: "hotels", label: "Hotels", icon: faHotel },
            { id: "more", label: "More", icon: faEllipsisH, isAction: true },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                if (tab.isAction && tab.id === "more") {
                  setShowMoreMenu(!showMoreMenu);
                } else {
                  setActiveMobileTab(tab.id);
                  setShowMoreMenu(false);
                }
              }}
              className="relative flex flex-col items-center justify-center flex-1 h-full pt-1"
            >
              <div className={`flex items-center justify-center w-14 h-8 rounded-full transition-all duration-300 ${(activeMobileTab === tab.id && !tab.isAction) || (tab.id === "more" && showMoreMenu) ? "bg-teal-100/80 text-teal-700" : "text-gray-500"
                }`}>
                <FontAwesomeIcon
                  icon={tab.icon}
                  className={`text-[20px] transition-transform duration-300 ${(activeMobileTab === tab.id && !tab.isAction) || (tab.id === "more" && showMoreMenu) ? "scale-110" : ""}`}
                />
              </div>
              <span className={`text-[11px] mt-1 tracking-wide transition-colors duration-300 ${(activeMobileTab === tab.id && !tab.isAction) || (tab.id === "more" && showMoreMenu) ? "text-teal-700 font-bold" : "text-gray-500 font-medium"
                }`}>
                {tab.label}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

